import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { btnSize, btnType, Button, colors, fontWeight, Text, textLevel } from '../../packages';
import { sxStyles } from './StepProcess.styles';
import SocketClient from '../../utils/SocketClient';
import { initializeLabAutomation } from '../../services/report-service/reportService.service';
import StepItem from './StepItem';
import { automationSteps } from './steps.mock';

const StepProcess = ({
  noteId,
  setIsReadOnlyMode,
  setShowStepProcess,
  onClose,
  fetchNotes,
  fetchNotesRecommendations,
  lab,
}) => {
  const [socket, setSocket] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  // This switch will kill actual lab submissions and will only present a mock list of submission progress statuses.
  // Must be false for production use.
  const shouldUseMock = false;
  const [automationMessages, setAutomationMessages] = useState(shouldUseMock ? automationSteps : []);
  const [retryCount, setRetryCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [isAutomationCompleted, setIsAutomationCompleted] = useState(false);
  const [skipRetry, setSkipRetry] = useState(false);

  const handleLabCorpIntegration = async noteId => {
    const labName = lab || 'Labcorp';
    const labLower = lab?.toLowerCase() || 'labcorp';
    try {
      const response = await initializeLabAutomation({ notesId: noteId }, { type: labLower });
      const { sessionId, isQueued } = response.data;
      setSessionId(sessionId);
      if (isQueued) {
        createUniqueAutomationMessage({
          step: `${labLower}_queued_update`,
          status: 'in_progress',
          message: `Give us 2-3 minutes. We'll start processing your request shortly`,
          timestamp: new Date(),
        });
      }
    } catch (error) {
      const errorMessage =
        error?.data?.error?.message ||
        error?.data?.error ||
        `Unexpected error during ${labName} integration.`;
      createUniqueAutomationMessage({
        status: 'error',
        message: errorMessage,
        timestamp: new Date(),
      });
      setHasError(true);
      setIsReadOnlyMode(false);
    }
  };

  useEffect(() => {
    if (noteId && !shouldUseMock) {
      void handleLabCorpIntegration(noteId);
    }
  }, [noteId]);

  const containerRef = useRef(null);
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [automationMessages]);

  const activeStepName = automationMessages[automationMessages.length - 1]?.message || '';

  const createUniqueAutomationMessage = useCallback(
    rawMessage => {
      if (hasError) {
        return;
      }

      if (rawMessage.subStepMessage) {
        setAutomationMessages(prev => {
          const eventsArray = [...prev];
          const existingStepIndex = eventsArray.findIndex(event => event.step === rawMessage.step);

          if (existingStepIndex !== -1) {
            const existingStep = eventsArray[existingStepIndex];

            if (!existingStep.subSteps) {
              existingStep.subSteps = [];
            }

            const existingSubStepIndex = existingStep.subSteps.findIndex(
              sub => sub.subStep === rawMessage.subStep,
            );

            if (existingSubStepIndex !== -1) {
              existingStep.subSteps[existingSubStepIndex] = {
                subStep: rawMessage.subStep,
                subStepMessage: rawMessage.subStepMessage,
                subStepStatus: rawMessage.subStepStatus,
                timestamp: new Date(),
              };
            } else {
              existingStep.subSteps.push({
                subStep: rawMessage.subStep,
                subStepMessage: rawMessage.subStepMessage,
                subStepStatus: rawMessage.subStepStatus,
                timestamp: new Date(),
              });
            }
          } else {
            eventsArray.push({
              step: rawMessage.step,
              status: rawMessage.status,
              message: rawMessage.message,
              timestamp: new Date(),
              subSteps: [
                {
                  subStep: rawMessage.subStep,
                  subStepMessage: rawMessage.subStepMessage,
                  subStepStatus: rawMessage.subStepStatus,
                  timestamp: new Date(),
                },
              ],
            });
          }

          return eventsArray;
        });
      } else {
        if (rawMessage.message && rawMessage.message.includes('Lab order successfully created.')) {
          setSkipRetry(true);
        }

        const { status, message, step } = rawMessage;
        setAutomationMessages(prev => {
          const eventsArray = [...prev];
          const existingStepIndex = eventsArray.findIndex(msg => msg.step === step);

          if (existingStepIndex !== -1) {
            const existingStep = eventsArray[existingStepIndex];

            eventsArray[existingStepIndex] = {
              ...existingStep,
              status,
              message,
              timestamp: new Date(),
              ...(existingStep.subSteps && { subSteps: existingStep.subSteps }),
            };
          } else {
            eventsArray.push({
              step,
              status,
              message,
              timestamp: new Date(),
            });
          }

          return eventsArray;
        });
      }
    },
    [automationMessages, retryCount, hasError],
  );

  const closeSocket = (socket, labName) => {
    if (socket) {
      const labLower = labName?.toLowerCase() || 'labcorp';
      socket.off(`${labLower}_automation_start`);
      socket.off('automation_status_updates');
      socket.off(`${labLower}_automation_end`);
    }
  };

  useEffect(() => {
    return () => {
      closeSocket(socket, lab);
    };
  }, [socket, lab]);

  const startLabCorpAutomation = sessionId => {
    if (!sessionId) {
      console.error('No session id provided for lab automation');
      return;
    }

    const labLower = lab?.toLowerCase() || 'labcorp';
    const labName = lab || 'Labcorp';

    setHasError(false);
    setIsReadOnlyMode(true);

    try {
      const socketClient = SocketClient.getInstance();
      if (socketClient) {
        const connectedSocket = socketClient.getConnectedSocket();
        setSocket(connectedSocket);

        connectedSocket.on(`${labLower}_automation_start`, data => {
          if (data?.data?.sessionId !== sessionId) {
            return; // Ignore events for other sessions
          }
          createUniqueAutomationMessage({
            status: 'success',
            message: `Connected with ${labName}`,
            timestamp: new Date(),
          });
        });

        connectedSocket.on('automation_status_updates', data => {
          if (data?.data?.sessionId !== sessionId) {
            return; // Ignore events for other sessions
          }
          const eventData = data.data || data;
          const { status } = eventData;

          createUniqueAutomationMessage({
            status: eventData.status,
            message: eventData.message,
            subStepMessage: eventData.subStepMessage,
            subStepStatus: eventData.subStepStatus,
            step: eventData.step,
            subStep: eventData.subStep,
            timestamp: new Date(),
          });

          if (status === 'error') {
            setHasError(true);
            setIsReadOnlyMode(false);
            closeSocket(connectedSocket, lab);
          }
        });

        connectedSocket.on(`${labLower}_automation_end`, data => {
          if (data?.data?.sessionId !== sessionId) {
            return; // Ignore events for other sessions
          }
          createUniqueAutomationMessage({
            status: 'success',
            message: `Submission sent to ${labLower}`,
            timestamp: new Date(),
          });
          setIsReadOnlyMode(false);
          setIsAutomationCompleted(true);
        });
      } else {
        console.error('Failed to get socket client instance');
        setHasError(true);
        setIsReadOnlyMode(false);
      }
    } catch (error) {
      console.error('Failed to initialize socket:', error);
      setHasError(true);
      createUniqueAutomationMessage({
        status: 'error',
        message: `Failed to connect to ${labName}`,
        timestamp: new Date(),
      });
      setIsReadOnlyMode(false);
    }
  };

  const handleRetry = useCallback(() => {
    setAutomationMessages([]);
    setRetryCount(prev => prev + 1);
    setHasError(false);
    setIsReadOnlyMode(true);
    setSessionId(null);
    setIsAutomationCompleted(false);
    if (!shouldUseMock) {
      void handleLabCorpIntegration(noteId);
    }
  }, [retryCount]);

  const handleDone = () => {
    setShowStepProcess(false);
    fetchNotes();
    fetchNotesRecommendations?.();
    onClose();
  };

  useEffect(() => {
    if (sessionId) {
      startLabCorpAutomation(sessionId);
    }
  }, [sessionId]);

  return (
    <Box sx={sxStyles.stepProcessContainer}>
      <Box sx={sxStyles.innerContainer} ref={containerRef}>
        <Box sx={sxStyles.stepProcessHeader}>
          <Box sx={sxStyles.headerText}>
            <Text level={textLevel.XS} weight={fontWeight.EXTRA_BOLD} color={colors.neutral500}>
              STEPS IN PROCESS
            </Text>
            {activeStepName && (
              <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral700}>
                {activeStepName}
              </Text>
            )}
          </Box>
        </Box>

        {automationMessages.length === 0 && (
          <Box sx={sxStyles.connectingToLabcorpContainer}>
            <Text level={textLevel.S} weight={fontWeight.MEDIUM} color={colors.neutral500}>
              Connecting to {lab || 'Labcorp'}...
            </Text>
            <CircularProgress color="secondary" size={16} />
          </Box>
        )}

        <Box sx={sxStyles.stepsList}>
          {automationMessages.map((step, index) => {
            const subStepsLength = step.subSteps?.length || 0;
            const isLastStep = index === automationMessages.length - 1;
            const isLastSubStep = subStepIndex => subStepIndex === subStepsLength - 1;
            return (
              <StepItem
                key={index}
                step={step}
                isLastStep={isLastStep}
                subStepsLength={subStepsLength}
                isLastSubStep={isLastSubStep}
              />
            );
          })}
        </Box>
      </Box>
      <Box sx={sxStyles.buttonContainer}>
        {hasError && !skipRetry && retryCount < 1 && automationMessages.length > 0 ? (
          <Button variant={btnType.PRIMARY} size={btnSize.SMALL} onClick={handleRetry} sx={sxStyles.button}>
            Try again
          </Button>
        ) : (
          (isAutomationCompleted || skipRetry || (hasError && retryCount >= 1)) && (
            <Button variant={btnType.PRIMARY} size={btnSize.SMALL} onClick={handleDone} sx={sxStyles.button}>
              Done
            </Button>
          )
        )}
      </Box>
    </Box>
  );
};

export default StepProcess;
